// Mirrors the JSON shapes the CanXP training control plane returns
// (https://github.com/CanXPAI/training-backend, see app/schemas.py).
// Kept loose on purpose — the website only consumes a subset.

export type TrainingType = 'lora' | 'qlora' | 'full' | 'cpt';

export type GpuType =
  | 'rtx_3090'
  | 'rtx_4090'
  | 'a40'
  | 'a100_40gb'
  | 'a100_80gb'
  | 'l40s';

export type CloudMode = 'secure' | 'community' | 'any';

export type JobStatus =
  | 'created'
  | 'queued'
  | 'allocating_runpod'
  | 'runpod_starting'
  | 'worker_booting'
  | 'downloading_dataset'
  | 'preprocessing'
  | 'training'
  | 'evaluating'
  | 'uploading_artifacts'
  | 'completed'
  | 'shutting_down_runpod'
  | 'failed'
  | 'cancelled';

export const TERMINAL_STATUSES: ReadonlyArray<JobStatus> = ['completed', 'failed', 'cancelled'];

export interface SupportedModel {
  id: string;
  label: string;
  family: string;
  parameters_b: number;
  license: string;
  license_url: string;
  commercial_use: boolean;
  min_vram_gb_qlora: number;
  max_context: number;
  chat_template: string;
  notes?: string;
  // "standard" (default, ≤13B; available to any credit) or "enterprise"
  // (Frontier-tier-only models like Qwen 3.6 27B that need multi-GPU).
  // Admins can launch any tier regardless of this value.
  min_tier?: 'standard' | 'enterprise';
  // Forwarded from the registry — not consumed in the UI today but kept
  // on the type so consumers can introspect it for tooltips/badges.
  trust_remote_code?: boolean;
  inference_image?: string;
}

export interface JobSpec {
  customer_id?: string; // ignored by server (it forces auth.user.id)
  request_id?: string;
  dataset_uri: string;
  base_model: string;
  training_type: TrainingType;
  output_name: string;
  max_epochs?: number;
  learning_rate?: number;
  context_length?: number;
  validation_split?: number;
  quantize?: boolean;
  deploy_after_training?: boolean;
  gpu_type?: GpuType | null;
  // Multi-GPU count for Frontier-tier runs. Defaults to 1 server-side
  // when omitted. Backend clamps to [1, 4].
  gpu_count?: number;
  cloud_mode?: CloudMode;
  // Website-side only: identifies which one-time self-serve package credit
  // should be consumed for this job. The SSR server strips it before
  // forwarding the request to the training backend.
  package_id?: string;
  // Optional user-supplied HuggingFace token. If present, the worker
  // pushes the trained adapter to the user's personal HF account in
  // addition to canxp-ai. Stored on the user's Supabase profile row.
  huggingface_user_token?: string;
  extra?: Record<string, unknown>;
}

export interface TrainingJob {
  id: string;
  customer_id: string;
  request_id?: string | null;
  output_name: string;
  status: JobStatus;
  progress: number;
  stage?: string | null;
  error?: string | null;
  failure_reason?: string | null;
  base_model: string;
  training_type: TrainingType;
  dataset_uri: string;
  dataset_bytes?: number | null;
  dataset_examples?: number | null;
  max_epochs: number;
  learning_rate: number;
  context_length: number;
  validation_split: number;
  quantize: boolean;
  deploy_after_training: boolean;
  is_hosted?: boolean;
  hosted_at?: string | null;
  gpu_type?: GpuType | null;
  cloud_mode?: string | null;
  provider?: string | null;
  runpod_pod_id?: string | null;
  runpod_gpu_type?: string | null;
  runpod_pod_status?: string | null;
  metrics?: Record<string, unknown> | null;
  artifact_uri?: string | null;
  spec: Record<string, unknown>;
  pod_started_at?: string | null;
  pod_terminated_at?: string | null;
  queued_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobList {
  jobs: TrainingJob[];
  total: number;
  limit: number;
  offset: number;
}

export interface JobEvent {
  seq: number;
  ts: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  event_type: string;
  message?: string | null;
  payload?: Record<string, unknown> | null;
}

export interface TrainingMetricPoint {
  seq: number;
  ts: string;
  step: number | null;
  epoch: number | null;
  loss: number | null;
  eval_loss: number | null;
  learning_rate: number | null;
  grad_norm: number | null;
  ppl: number | null;
  tokens_per_sec_per_gpu: number | null;
  tokens_total: number | null;
}

export interface TrainingMetricsResponse {
  job_id: string;
  series: TrainingMetricPoint[];
  last_seq: number;
  total_steps: number | null;
}

export interface JobArtifact {
  id: string;
  kind: 'adapter' | 'merged_model' | 'quantized' | 'report' | 'log' | 'dataset_processed' | 'eval';
  path: string;
  size_bytes?: number | null;
  sha256?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
}
